import { useState, useCallback, useEffect } from "react";
import {
  FamilyStats,
  MemberStats,
  UpdateMemberStatsDto,
} from "@/types/familyStats";
import familyStatsService from "@/services/familyStats.service";
import { MemberInfo } from "@/types/member";

interface UseFamilyStatsProps {
  memberInfo: MemberInfo & { onBehalf: boolean };
}

export function useFamilyStats({ memberInfo }: UseFamilyStatsProps) {
  const [familyStats, setFamilyStats] = useState<FamilyStats | null>(null);
  const [memberStats, setMemberStats] = useState<MemberStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);

  // Reset states when member changes
  useEffect(() => {
    setFamilyStats(null);
    setMemberStats(null);
    setError(null);
    setFetched(false);
  }, [memberInfo?.id]);

  /**
   * Fetch family statistics including all member stats
   * Uses the logged-in user's family context
   */
  const fetchFamilyStats = useCallback(async () => {
    if (!memberInfo?.id) return null;

    setLoading(true);
    setError(null);
    try {
      if (!memberInfo.current_family) {
        throw new Error("No family context available");
      }

      const data = await familyStatsService.getFamilyStats();
      setFamilyStats(data);
      setFetched(true);
      return data;
    } catch (err: any) {
      const errorMsg = err?.message || "Error fetching family stats";
      console.error(errorMsg, err);
      setError(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, [memberInfo?.id, memberInfo?.current_family]);

  /**
   * Fetch statistics for a specific member
   * Uses the logged-in user's family context
   */
  const fetchMemberStats = useCallback(
    async (memberId?: string) => {
      if (!memberInfo?.id) return null;

      setLoading(true);
      setError(null);
      try {
        if (!memberInfo.current_family) {
          throw new Error("No family context available");
        }

        const targetMemberId = memberId || memberInfo.id;

        const data = await familyStatsService.getMemberStats(targetMemberId);
        setMemberStats(data);
        return data;
      } catch (err: any) {
        const errorMsg = err?.message || "Error fetching member stats";
        console.error(errorMsg, err);
        setError(errorMsg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [memberInfo?.id, memberInfo?.current_family]
  );

  /**
   * Update member statistics
   * Uses the logged-in user's family context
   */
  const updateMemberStats = useCallback(
    async (updateDto: UpdateMemberStatsDto) => {
      if (!memberInfo?.id) {
        throw new Error("Member info not available");
      }

      setLoading(true);
      setError(null);
      try {
        if (!memberInfo.current_family) {
          throw new Error("No family context available");
        }

        const data = await familyStatsService.updateMemberStats(updateDto);
        setFamilyStats(data);
        return data;
      } catch (err: any) {
        const errorMsg = err?.message || "Error updating member stats";
        setError(errorMsg);
        throw new Error(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [memberInfo?.id, memberInfo?.current_family]
  );

  return {
    familyStats,
    memberStats,
    loading,
    error,
    fetched,
    fetchFamilyStats,
    fetchMemberStats,
    updateMemberStats,
  } as const;
}
